// Readable versions of authored colours (WCAG 1.4.3 Contrast, 4.5:1).
// Same rule as the Java ColorContrastUtil: a colour that already passes is
// returned unchanged; one that fails becomes the site colour of its family
// (see PALETTE), or is darkened in its own hue just enough to pass. Hex and opaque rgb() values are understood; anything else
// (gradients, named colours, transparency) is returned as authored.
const MIN_RATIO = 4.5;

function parseHex(value) {
    const text = String(value || "").trim();
    const rgb = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*(?:1|1\.0+)\s*)?\)$/i.exec(text);
    if (rgb) {
        return [rgb[1], rgb[2], rgb[3]].map((v) => Math.min(255, parseInt(v, 10)));
    }
    const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(text);
    if (!m) {
        return null;
    }
    const hex = m[1].length === 3 ? m[1].replace(/./g, "$&$&") : m[1];
    return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
}

function luminance(rgb) {
    const c = rgb.map((v) => {
        const x = v / 255;
        return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

export function contrast(a, b) {
    const ra = parseHex(a);
    const rb = parseHex(b);
    if (!ra || !rb) {
        return null;
    }
    const la = luminance(ra);
    const lb = luminance(rb);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

function toHsl([r, g, b]) {
    const [rr, gg, bb] = [r / 255, g / 255, b / 255];
    const max = Math.max(rr, gg, bb);
    const min = Math.min(rr, gg, bb);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === rr) {
            h = (gg - bb) / d + (gg < bb ? 6 : 0);
        } else if (max === gg) {
            h = (bb - rr) / d + 2;
        } else {
            h = (rr - gg) / d + 4;
        }
        h /= 6;
    }
    return [h, s, l];
}

function fromHsl(h, s, l) {
    if (s === 0) {
        return [l, l, l].map((v) => Math.round(v * 255));
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hue = (t) => {
        let x = t;
        if (x < 0) {
            x += 1;
        }
        if (x > 1) {
            x -= 1;
        }
        if (x < 1 / 6) {
            return p + (q - p) * 6 * x;
        }
        if (x < 1 / 2) {
            return q;
        }
        if (x < 2 / 3) {
            return p + (q - p) * (2 / 3 - x) * 6;
        }
        return p;
    };
    return [hue(h + 1 / 3), hue(h), hue(h - 1 / 3)].map((v) => Math.round(v * 255));
}

// The site's text-safe colours, by colour family (same as ColorContrastUtil):
// a failing authored blue, red, yellow, green or grey becomes the colour the
// stylesheets use for that family, so authored and coded colours look the same.
// Hue ranges are in degrees; other hues are darkened in their own hue.
const PALETTE = [
    [180, 245, "#0c73b4"], // $blue-400-text
    [335, 360, "#db0011"], // $red-300 (HSBC red)
    [0, 15, "#db0011"],
    [35, 65, "#936d00"], // $yellow-300-text
    [75, 165, "#007f05"], // green (Tenders "Open")
];
const GREY_TEXT = "#737373"; // $black-400

function paletteColour([h, s]) {
    if (s < 0.15) {
        return GREY_TEXT;
    }
    if (s < 0.25) {
        return null;
    }
    const degrees = h * 360;
    const entry = PALETTE.find(([from, to]) => degrees >= from && degrees < to);
    return entry ? entry[2] : null;
}

export function readableOn(value, background = "#ffffff") {
    const rgb = parseHex(value);
    if (!rgb || contrast(value, background) === null || contrast(value, background) >= MIN_RATIO) {
        return value;
    }
    const [h, s, l] = toHsl(rgb);
    const family = paletteColour([h, s]);
    if (family && contrast(family, background) >= MIN_RATIO) {
        return family;
    }
    for (let light = l; light > 0; light -= 0.005) {
        const candidate = "#" + fromHsl(h, s, light).map((v) => v.toString(16).padStart(2, "0")).join("");
        if (contrast(candidate, background) >= MIN_RATIO) {
            return candidate;
        }
    }
    return "#000000";
}

export function readableOnWhite(value) {
    return readableOn(value, "#ffffff");
}

// Text colour + background pair: if they fail, darken whichever side is the
// colour (white text keeps white and gets a darker background; coloured text
// on a white/near-white background gets a darker text colour).
export function readablePair(text, background) {
    const ratio = contrast(text, background);
    if (ratio === null || ratio >= MIN_RATIO) {
        return { text, background };
    }
    if (contrast(text, "#ffffff") < 1.1) {
        return { text, background: readableOn(background, text) };
    }
    if (contrast(background, "#ffffff") < 1.25) {
        return { text: readableOn(text, background), background };
    }
    return { text, background };
}
